import React, { type ReactNode } from "react";
import Image from "next/image";
import { Box, Flex, AspectRatio, type FlexProps } from "@chakra-ui/react";
import Balancer from "react-wrap-balancer";
import { Section } from "components/section";
import { TextTitle, type TextTitleProps } from "ui/text";
import posterImage from "../../../public/assets/demo-embed/poster.png";
import { NavatticDemoModal } from "./navattic-demo-modal";

type DemoEmbedProps = {
  demoId?: string;
  demoName?: string;
  eyebrow?: string;
  title?: string;
  description?: ReactNode;
  /**
   * Size of `TextTitle` that renders the title and description
   * @default "lg"
   */
  titleSize?: TextTitleProps["size"];
  imageProps?: FlexProps;
};

/**
 * Because we currently only support hightouch on the web,
 * this embeded demo will always be hidden on mobile (sm) and tablet (md) screens.
 */
export function DemoEmbedHalf({
  title = "See how it works",
  description = "Take our virtual product tour to see how you can get started building and syncing customer data in minutes, not months.",
  titleSize = "lg",
  demoId,
  demoName,
  eyebrow,
  imageProps,
}: DemoEmbedProps) {
  return (
    <Section
      display={{ base: "none", lg: "flex" }}
      bg="new.blue.950"
      themeBgColor="new.blue.950"
      theme="dark"
      py={0}
      overflow="hidden"
    >
      <Flex boxSize="100%" align="stretch" gap={{ base: 16, xl: 20 }}>
        <Box flex={1} maxW={{ base: "400px", xl: "460px" }} py={{ base: 32, xl: 36 }}>
          <TextTitle
            theme="dark"
            size={titleSize}
            chip={eyebrow ? { text: eyebrow } : undefined}
            title={<Balancer>{title}</Balancer>}
            description={description}
            descriptionAs={typeof description === "string" ? "p" : "div"}
            actions={
              <NavatticDemoModal hasRightChevron demoId={demoId} demoName={demoName}>
                Start exploring
              </NavatticDemoModal>
            }
          />
        </Box>

        <Flex flex={1} pos="relative" boxSize="100%">
          <Flex pos="absolute" left={0} bottom={0} w="1200px" h="88%" maxH="548px" {...imageProps}>
            <AspectRatio
              pos="relative"
              ratio={1000 / 456}
              w="100%"
              borderTopRadius="md"
              overflow="hidden"
              _before={{
                content: "''",
                position: "absolute",
                inset: 0,
                top: "auto",
                h: "40%",
                bgGradient: "linear(to-t, rgba(25, 26, 28, 0.1), transparent)",
                zIndex: 1,
              }}
            >
              <Image
                fill
                sizes="100%"
                src={posterImage}
                alt="Hightouch Audiences user interface."
                placeholder="blur"
                style={{ objectFit: "cover", objectPosition: "top left" }}
              />
            </AspectRatio>
          </Flex>
        </Flex>
      </Flex>
    </Section>
  );
}
